// VERCHK-1 (2026-07-12) — "New version available" toast.
//
// Problem: Cloudflare Pages serves a new bundle after every staging deploy,
// but an admin who keeps the tab open (or whose browser cached index.html)
// keeps running the old JS until they hard-refresh. Symptoms: "I can't see
// the new panel" support pings that are really just stale bundles.
//
// Approach: CRA content-hashes the entry bundle (static/js/main.<hash>.js),
// so the served index.html always references the *current* build. We poll
// index.html (cache: no-store) and compare the main-bundle path it references
// against the one this page actually loaded. Mismatch === a newer build is
// live → show a persistent sonner toast with a Refresh action. No backend
// or build-pipeline changes needed, and no version needs to be stamped.
//
// Cadence: every 5 minutes + on window focus / tab-visible, throttled to at
// most one check per 60s. Never force-reloads (unsaved form state is sacred);
// the toast sticks until the user refreshes or dismisses it. One toast per
// new hash (fixed toast id + notified-set).

import { useEffect } from "react";
import { toast } from "sonner";

const POLL_MS = 5 * 60 * 1000; // 5 minutes
const MIN_GAP_MS = 60 * 1000;  // throttle focus/visibility re-checks
const MAIN_RE = /static\/js\/main\.[A-Za-z0-9]+\.js/;

export default function useVersionCheck() {
  useEffect(() => {
    // The bundle this page is actually running. In dev (webpack-dev-server)
    // there is no content-hashed main.<hash>.js — bail out entirely.
    const loadedSrc = Array.from(document.scripts)
      .map((s) => s.getAttribute("src") || "")
      .find((src) => MAIN_RE.test(src));
    if (!loadedSrc) return undefined;
    const loadedMain = loadedSrc.match(MAIN_RE)[0];

    let lastCheck = 0;
    let cancelled = false;
    const notified = new Set();

    const check = async () => {
      const now = Date.now();
      if (now - lastCheck < MIN_GAP_MS) return;
      lastCheck = now;
      try {
        const res = await fetch("/index.html", { cache: "no-store" });
        if (!res.ok) return;
        const html = await res.text();
        const servedMain = html.match(MAIN_RE)?.[0];
        if (!servedMain || cancelled) return;
        if (servedMain !== loadedMain && !notified.has(servedMain)) {
          notified.add(servedMain);
          toast.info("A new version of the CRM is available.", {
            id: "verchk-new-build", // fixed id → never stacks duplicates
            duration: Infinity,
            action: {
              label: "Refresh",
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        // Network blip / offline — silently try again next cycle.
      }
    };

    const interval = setInterval(check, POLL_MS);
    const onFocus = () => check();
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
}
